import { MapPin, Clock, User, Car, Building2, Euro, Eye, Edit, Copy, Trash2, Navigation, UserCheck, Flag, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Mobile-friendly ride card component
 * Used for displaying rides in card format on mobile devices
 */
export default function MobileRideCard({ 
  ride, 
  formatDateTime, 
  getStatusColor,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleLive,
  onArrivedPickup,
  onCustomerPickedUp,
  onArrivedDropoff,
  showTrackingButtons = true
}) {
  const statusColors = {
    active: 'bg-sky-100 text-sky-800 border-sky-200',
    in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
    completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    no_show: 'bg-amber-100 text-amber-800 border-amber-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200',
    delayed: 'bg-orange-100 text-orange-800 border-orange-200'
  };

  const statusColor = statusColors[ride.status] || 'bg-slate-100 text-slate-800 border-slate-200';

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`mobile-ride-card-${ride.id}`}>
      {/* Header: Customer & Status */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{ride.customer_name || 'N/A'}</h3>
            <p className="text-sm text-slate-500 font-mono mt-0.5">
              {ride.date_time ? formatDateTime(ride.date_time) : 'Not scheduled'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColor}`}>
              {ride.status ? ride.status.replace('_', ' ') : 'Unknown'}
            </span>
            {/* LIVE indicator */}
            {ride.is_live ? (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-200">
                LIVE
              </span>
            ) : (
              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full border border-gray-200">
                NOT LIVE
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Route */}
      <div className="p-4 bg-slate-50 space-y-2">
        <a 
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.pickup_location || '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-2 text-sm"
        >
          <span className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></span>
          <span className="text-slate-700 break-words">{ride.pickup_location || 'N/A'}</span>
        </a>
        <a 
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.dropoff_location || '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-2 text-sm"
        >
          <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0"></span>
          <span className="text-slate-700 break-words">{ride.dropoff_location || 'N/A'}</span>
        </a>
      </div>

      {/* Details Grid */}
      <div className="p-4 grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-slate-400" />
          <span className="text-slate-600 truncate">{ride.assigned_driver_name || 'Unassigned'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Car className="h-4 w-4 text-slate-400" />
          <span className="text-slate-600 truncate">{ride.vehicle_plate || 'N/A'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-slate-400" />
          <span className="text-slate-600 truncate">{ride.partner || '—'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Euro className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-900">€{ride.price?.toFixed(2) || '0.00'}</span>
          {ride.driver_commission > 0 && (
            <span className="text-xs text-emerald-600">(+€{ride.driver_commission?.toFixed(2)})</span>
          )}
        </div>
      </div>

      {/* Tracking Buttons (for in-progress rides) */}
      {showTrackingButtons && ride.status !== 'completed' && ride.status !== 'cancelled' && (
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {!ride.arrived_at_pickup && (
            <Button
              onClick={() => onArrivedPickup?.(ride.id)}
              size="sm"
              className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-white text-xs"
            >
              <Navigation className="mr-1 h-3 w-3" />
              Arrived Pickup
            </Button>
          )}
          {ride.arrived_at_pickup && !ride.picked_up_customer && (
            <Button
              onClick={() => onCustomerPickedUp?.(ride.id)}
              size="sm"
              className="flex-1 h-9 bg-purple-600 hover:bg-purple-700 text-white text-xs"
            >
              <UserCheck className="mr-1 h-3 w-3" />
              Customer Picked
            </Button>
          )}
          {ride.picked_up_customer && !ride.arrived_at_dropoff && (
            <Button
              onClick={() => onArrivedDropoff?.(ride.id)}
              size="sm"
              className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            >
              <Flag className="mr-1 h-3 w-3" />
              Complete Ride
            </Button>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="px-4 pb-4 flex items-center gap-1 border-t border-slate-100 pt-3">
        <button
          onClick={() => onView?.(ride)}
          className="flex-1 flex items-center justify-center gap-1 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors text-xs font-medium"
          data-testid={`mobile-view-ride-${ride.id}`}
        >
          <Eye className="h-3.5 w-3.5" />
          View
        </button>
        {/* Toggle LIVE button */}
        <button
          onClick={() => onToggleLive?.(ride.id, ride.is_live)}
          className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg transition-colors text-xs font-medium ${
            ride.is_live 
              ? 'text-green-600 hover:bg-green-50' 
              : 'text-gray-400 hover:bg-gray-100'
          }`}
          title={ride.is_live ? 'Click to set NOT LIVE' : 'Click to set LIVE'}
          data-testid={`mobile-toggle-live-${ride.id}`}
        >
          <Zap className="h-3.5 w-3.5" />
          Live
        </button>
        <button
          onClick={() => onDuplicate?.(ride)}
          className="flex-1 flex items-center justify-center gap-1 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors text-xs font-medium"
        >
          <Copy className="h-3.5 w-3.5" />
          Copy
        </button>
        {onEdit && (
        <button
          onClick={() => onEdit(ride)}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          data-testid={`mobile-edit-ride-${ride.id}`}
        >
          <Edit className="h-4 w-4" />
        </button>
        )}
        <button
          onClick={() => onDelete?.(ride)}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
